// The countdown and the two things somebody does about it.
//
// One component, so "the event details should be consistent with the event
// page" is true structurally rather than by two screens happening to be built
// the same way this week. ⚠️ Only the attendee page draws it today — the events
// map that was its second caller is retired (see `src/index.js`) — and it is
// still a component rather than inlined there for that reason: the campus map's
// own sheet is the next thing that would need this countdown.
import { useState, useEffect } from 'react';
import { eventStart, fmtWhen, downloadIcs } from '../utils/eventTime.js';

/**
 * Days / hours / minutes / seconds, as ONE block.
 *
 * Seconds are there for the day itself, when the answer people want is "have I
 * missed it" and a countdown that only moves once a minute looks stopped. It
 * owns its own timer so the ticking re-renders this and not the map behind it.
 */
export function CountdownClock({ when }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!when) return null;

  const ms = when.getTime() - now;
  if (ms <= 0) return <div className="ea-clock live">Happening now</div>;

  const total = Math.floor(ms / 1000);
  const parts = [
    { n: Math.floor(total / 86400), label: 'days' },
    { n: Math.floor(total / 3600) % 24, label: 'hours' },
    { n: Math.floor(total / 60) % 60, label: 'min' },
    { n: total % 60, label: 'sec' },
  ];
  return (
    <div className="ea-clock">
      {parts.map(p => (
        <div className="ea-unit" key={p.label}>
          <strong>{String(p.n).padStart(2, '0')}</strong>
          <span>{p.label}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * The share mark, drawn.
 *
 * It was a `⤴` character, which is a Unicode arrow on a desktop and a coloured
 * emoji on a phone — the one place this button actually gets pressed. A drawn
 * mark is the same picture everywhere and matches the stroke weight of every
 * other icon on these screens.
 */
function ShareMark() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 15V4M12 4 8 8M12 4l4 4" />
      <path d="M5 13v5.5A1.5 1.5 0 0 0 6.5 20h11a1.5 1.5 0 0 0 1.5-1.5V13" />
    </svg>
  );
}

/** Add to calendar + Share, with the countdown beside them. */
export function EventActions({ event, shareUrl, extra = null }) {
  const [shared, setShared] = useState('');

  const share = async () => {
    const url = shareUrl || (typeof window !== 'undefined' ? window.location.href : '');
    const data = { title: event.name, text: `${event.name} — ${fmtWhen(event)}`, url };
    // The native sheet is the right answer on a phone, which is where a link to
    // an event actually gets passed on. Copying is the desktop fallback, and
    // also what happens when somebody dismisses the share sheet.
    try {
      if (navigator.share) { await navigator.share(data); return; }
    } catch { /* dismissed — fall through to copying */ }
    try {
      await navigator.clipboard.writeText(url);
      setShared('Link copied');
    } catch { setShared(url); }
    setTimeout(() => setShared(''), 2400);
  };

  return (
    <>
      <div className="ea-row">
        <CountdownClock when={eventStart(event)} />
        <div className="ea-btns">
          <button className="ea-btn" onClick={() => downloadIcs(event)} disabled={!eventStart(event)}>
            Add to calendar
          </button>
          <button className="ea-btn ghost" onClick={share}>
            <ShareMark /> Share
          </button>
          {extra}
        </div>
      </div>
      {shared && <div className="ea-shared">{shared}</div>}
    </>
  );
}

/** Styles, injected by whichever page mounts this. */
export const EVENT_ACTIONS_CSS = `
.ea-row { display:flex; gap:10px; align-items:stretch; flex-wrap:wrap; }
.ea-row .ea-clock { flex:1 1 240px; }
.ea-btns { display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
.ea-shared { font-size:12.5px; color:#15803d; font-weight:700; }

/* Days : hours : min : sec as ONE block — it is a single reading. Tabular
   figures so the seconds ticking over does not shuffle the row sideways. */
.ea-clock { display:flex; background:#f6f7f9; border-radius:12px; padding:6px 4px;
  align-items:center; }
.ea-clock.live { justify-content:center; padding:12px; font-weight:800; color:#15803d; }
.ea-unit { flex:1; padding:2px 4px; text-align:center; position:relative; }
.ea-unit strong { display:block; font-size:22px; font-weight:800; line-height:1.1;
  font-variant-numeric:tabular-nums; font-feature-settings:"tnum"; }
.ea-unit span { font-size:11px; color:#6b7280; text-transform:uppercase; letter-spacing:.05em; }
/* Hairline separators inside the one block, instead of gaps between four. */
.ea-unit + .ea-unit::before { content:''; position:absolute; left:0; top:15%; bottom:15%;
  width:1px; background:rgba(15,18,23,.09); }

.ea-btn { background:#4f46e5; color:#fff; border:none; border-radius:10px; padding:10px 16px;
  font:inherit; font-size:14px; font-weight:700; cursor:pointer;
  display:inline-flex; align-items:center; justify-content:center; gap:7px; }
.ea-btn:disabled { opacity:.5; cursor:default; }
.ea-btn.ghost { background:#fff; color:#15181d; border:1.5px solid #e2e5ea; }

@media (max-width:620px) {
  .ea-unit strong { font-size:18px; }
  .ea-row .ea-clock { flex-basis:100%; }
  .ea-btns { width:100%; }
  .ea-btns .ea-btn { flex:1; }
}
`;
